import { useQuery, useMutation, useQueryClient } from 'react-query';
import { predictionArbitrageManagerAxiosApi, predictionManagerAxiosApi } from 'src/Config/axiosApi';
import { PositionsGroupedByPair, PerformanceData, PredictionCashflow } from '../types';

interface PositionStats {
  total: number;
  open: number;
  closed: number;
  expired: number;
  totalInvested: number;
  totalUnrealizedPnl: number;
}

interface PositionsFilters {
  status?: string;
  platform?: string;
}

export function usePositionsGrouped(filters?: PositionsFilters) {
  return useQuery(
    ['positions-grouped', filters],
    async () => {
      const { data } = await predictionArbitrageManagerAxiosApi.get<
        PositionsGroupedByPair[]
      >('/positions/grouped', { params: filters });
      const groups = data.filter(g => g._id != null);
      const unmatched = data.filter(g => g._id == null);
      return { groups, unmatched };
    },
    {
      keepPreviousData: true,
      // Open positions can close outside this view; poll so stale rows disappear automatically.
      refetchInterval: filters?.status === 'open' ? 10000 : false,
      refetchIntervalInBackground: filters?.status === 'open',
    }
  );
}

export function usePositionsEnded() {
  return useQuery(
    ['positions-ended'],
    async () => {
      const { data } = await predictionArbitrageManagerAxiosApi.get<
        PositionsGroupedByPair[]
      >('/positions/grouped', { params: { status: 'ended' } });
      return data;
    },
    { keepPreviousData: true }
  );
}

export function usePositionStats() {
  return useQuery(['position-stats'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PositionStats>(
        '/positions/stats'
      );
    return data;
  });
}

export function usePerformance() {
  return useQuery(['position-performance'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PerformanceData>(
        '/positions/performance'
      );
    return data;
  }, { refetchInterval: 30000 });
}

export function useCashflows() {
  return useQuery(['position-cashflows'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PredictionCashflow[]>(
        '/positions/cashflows'
      );
    return data;
  });
}

export function useCreateCashflow() {
  const qc = useQueryClient();
  return useMutation(
    async (body: { platform: string; type: string; amountUsd: number; note?: string; date?: string }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.post('/positions/cashflows', body);
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('position-cashflows');
        qc.invalidateQueries('position-performance');
      }
    }
  );
}

export function useUpdatePairThreshold() {
  const qc = useQueryClient();
  return useMutation(
    async ({ pairId, threshold }: { pairId: string; threshold: number }) => {
      const { data } = await predictionManagerAxiosApi.patch(
        `/pairs/${pairId}`,
        { maxInvestmentThreshold: threshold }
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('positions-grouped');
      }
    }
  );
}

export function useUpdatePairExpiry() {
  const qc = useQueryClient();
  return useMutation(
    async ({ pairId, expiryDate }: { pairId: string; expiryDate: string }) => {
      const { data } = await predictionManagerAxiosApi.patch(`/pairs/${pairId}`, {
        expiryDate,
      });
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('positions-grouped');
      }
    }
  );
}

export function useSyncPositions() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } =
        await predictionArbitrageManagerAxiosApi.post('/positions/sync');
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('positions-grouped');
        qc.invalidateQueries('position-stats');
        qc.invalidateQueries('position-performance');
      }
    }
  );
}
